import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, ViewChild } from "@angular/core";
import { CommonModule, DatePipe } from "@angular/common";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { ManageAssessmentApiService, AssessmentItem } from "../../../../../core/services/api/manage-assessment-api.service";
import { ApiService, Region, Country } from "../../../../../core/services/api/api.service";
import { SharedModule } from "../../../../../shared/shared_module";
import { TableColumn, TableComponent, FilterConfigType, TableButton } from "../../../../../shared/components/table/table.component";
import { LazyLoadEvent, MessageService } from "primeng/api";
import { Subject } from "rxjs";
import { takeUntil, finalize } from "rxjs/operators";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { TruncatePipe } from "../../../../../shared/pipes/truncate.pipe";
import { AssessmentMailPopupComponent } from "../assessment-mail-popup/assessment-mail-popup.component";
import { ManageAssessmentDetailPopupComponent } from "../manage-assessment-detail-popup/manage-assessment-detail-popup.component";
import { OverlayPanel } from 'primeng/overlaypanel';
import { ManageAssessmentResponseFormComponent } from '../manage-assessment-response-form/manage-assessment-response-form.component';
import { ManageResponseAssignPopupComponent } from "../manage-response-assign-popup/manage-response-assign-popup.component";
import { SuccessComponent } from "../../../../../shared/components/success/success.component";
import { AssessmentConfirmationPopupComponent, ConfirmationPopupConfig } from '../assessment-confirmation-popup/assessment-confirmation-popup.component';
import { saveAs } from 'file-saver';

type ConfirmationActionType = 'revise' | 'markAsCompleted' | 'cancelAssessment' | 'no_impact' | 'delete' | 'duplicate' | 'duplicate_draft' | 'export_without_attachment';

export interface StatusStyle {
  label: string;
  color: string;
  backgroundColor: string;
}

interface RawResponseForm {
  response_mapping_id: number;
  country_id: number;
  region_id?: number; // optional: backend may omit, used for region lookup
  ra_colleague_names: string[];
  impact_status: string | null;
  response_status: string;
  started_at_timestamp: any;
  received_at_timestamp: any;
  response_version_number: number;
  assigned_to_user_id: number;
}

@Component({
  selector: "app-assessment-details",
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SharedModule,
    TableComponent,
    TruncatePipe,
    AssessmentMailPopupComponent,
    ManageAssessmentDetailPopupComponent,
    ManageAssessmentResponseFormComponent,
    ManageResponseAssignPopupComponent,
    SuccessComponent,
    AssessmentConfirmationPopupComponent,
  ],
  templateUrl: "./assessment-details.component.html",
  styleUrls: ["./assessment-details.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DatePipe, MessageService],
})
export class AssessmentDetailsComponent implements OnInit, OnDestroy {
  assessmentId: number | null = null;
  historyId: number | null = null;
  assessmentDetails: any | null = null;
  loadingDetails: boolean = true;
  loadingItems: boolean = false;
  activeTab: "Total" | "Responded" | "Pending" = "Total";
  totalItemsCount: number = 0;
  respondedItemsCount: number = 0;
  pendingItemsCount: number = 0;
  assessmentItems: AssessmentItem[] = [];
  itemColumns: TableColumn[] = [];
  itemGlobalFilterFields: string[] = [];
  itemFiltersConfig: FilterConfigType[] = [];
  itemFiltersValue: { [key: string]: any } = {};
  itemActions: any[] = [];
  itemTotalRecords: number = 0;
  itemPage: number = 1;
  itemPageSize: number = 10;
  itemLastLazyLoadEvent: LazyLoadEvent | null = null;
  countries: Country[] = [];
  regions: Region[] = [];
  allCountries: Country[] = [];
  versionDetails: any = [];
  private destroy$ = new Subject<void>();
  private readonly defaultStatusStyle: StatusStyle = {
    label: "",
    color: "white",
    backgroundColor: "var(--action-button-grey)",
  };
  private readonly statusStyleMap: Map<string, StatusStyle> = new Map<
    string,
    StatusStyle
  >([
    [
      "In Progress",
      { label: "In Progress", color: "white", backgroundColor: "#4E46B4" },
    ],
    [
      "Submitted",
      { label: "Submitted", color: "white", backgroundColor: "#4E46B4" },
    ],
    ["Draft", { label: "Draft", color: "black", backgroundColor: "#F0D413" }],
    [
      "Completed",
      { label: "Completed", color: "white", backgroundColor: "#395C22" },
    ],
    [
      "Cancelled",
      { label: "Cancelled", color: "white", backgroundColor: "#AE2A30" },
    ],
    [
      "Deleted",
      { label: "Deleted", color: "white", backgroundColor: "#F36F2A" },
    ],
  ]);
  fromAction: boolean = false;
  fromVersion: boolean = false;
  showMailPopupDialog: boolean = false;
  displayRcaDetailsPopup: boolean = false;
  displayAssessmentDetailsPopup: boolean = false;
  mappingIdValue: any | null = null;
  assessmentDetailsPopupData: any | null = null;
  // Overlay Panel properties
  @ViewChild("op") overlayPanel!: OverlayPanel; // Reference to the p-overlayPanel
  overlayPanelData: string[] = [];
  overlayPanelTitle: string = "";
  displayRcaDetailsFormPopup: boolean = false;
  displayRcaAssignPopup: boolean = false;

  // Add a property to store the global search term for assessment items
  itemGlobalSearchTerm: string = "";

  customerDetailsSuccessMessage: string = "";
  customerDetailsSuccessDialogTitle: string = "Success!";
  showCustomerDetailsSuccessDialog: boolean = false;
  mailPopupData: any | null = null;
  showBulkActionButton: boolean = false;
  bulkActionButtonConfig: TableButton = {
    label: "No Impact",
    action: "bulkAction",
  };
  selectedAssessmentItems: any[] = [];
  showConfirmationPopup: boolean = false;
  confirmationConfig: ConfirmationPopupConfig | null = null;
  private pendingAction: {
    type: ConfirmationActionType;
    row: any;
  } | null = null;
  UserRole = {
    RAAdmin: "RA Admin",
    LocalRA: "Local RA (Bayer RA colleague)",
    LocalRA1: "Local RA (Dealer & Distributor)",
    GlobalRA: "Global RA",
  };
  currentUserRole: any = "";
  currentUserId: any;
  
  tableSelectionMode: "multiple" | "single" | undefined;
  isExporting: boolean = false;
  isReviseResponse: boolean = false;
  isReviseCheck: boolean = false;

  canShowStartAssignButtons(rowData: any): boolean {
    const isRelevantStatus = rowData.response_status === 'Yet to Start' || 
                              rowData.response_status === 'Ongoing' || 
                              rowData.response_status === 'Submitted';
    const isCurrentUserLocalRa = rowData.assigned_to_user_id === this.currentUserId;
    
    // For Global RA and RA Admin with assigned responses:
    // Return false to show View/Change Assignee/Remove Assignee buttons
    if ((this.currentUserRole === this.UserRole.GlobalRA || this.currentUserRole === this.UserRole.RAAdmin) 
        && rowData.assigned_to_user_id) {
      return false;
    }
    
    // For Local RA: Only show Start/Resume buttons if they are the assigned user
    if ((this.currentUserRole === this.UserRole.LocalRA || this.currentUserRole === this.UserRole.LocalRA1) 
        && isRelevantStatus && isCurrentUserLocalRa) {
      return true;
    }
    
    // Default: Don't show Start/Resume buttons
    return false;
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private manageAssessmentApiService: ManageAssessmentApiService,
    private cdr: ChangeDetectorRef,
    private datePipe: DatePipe,
    private messageService: MessageService,
    private apiService: ApiService
  ) {}

  ngOnInit(): void {
    const storageItem: any = localStorage.getItem("profile");
    this.currentUserRole = JSON.parse(storageItem)?.userRole || "";
    this.currentUserId = JSON.parse(storageItem)?.userId || '';
    this.setTableSelectionMode();
    this.assessmentId = Number(this.route.snapshot.paramMap.get("id"));
    // Subscribe to query parameters
    this.route.queryParams
      .pipe(takeUntil(this.destroy$))
      .subscribe((params) => {
        this.fromAction = params["fromAction"];
        this.fromVersion = params["fromVersion"];
        this.historyId = params["history_id"] ? Number(params["history_id"]) : null;
        this.cdr.detectChanges();
      });
    if (this.fromVersion) {
      this.manageAssessmentApiService
        .getRcaVersionHistory(this.assessmentId)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            if (response?.history && response?.history.length > 0) {
              this.versionDetails = response?.history || [];
            }
          },
        });
    }
    this.loadDropdownData()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          if (this.assessmentId) {
            this.loadAssessmentDetails(this.assessmentId);
            this.initializeItemTableConfig();
            // Don't load assessment items here - wait for lazy load event
          } else {
            this.router.navigate(["/manage-assessment"]);
          }
        },
        error: (err) => {
          console.error("Failed to load initial data (regions/countries)", err);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: "Failed to load necessary data. Please try again.",
            life: 5000,
          });
          this.router.navigate(["/manage-assessment"]);
        },
      });
  }

  /**
   * Sets the table SelectionMode based on the currentUserRole.
   */
  setTableSelectionMode(): void {
    if (this.currentUserRole && this.currentUserRole.startsWith("Local RA")) {
      this.tableSelectionMode = "multiple";
    } else {
      this.tableSelectionMode = "single"; // Or 'single' if you want single selection for other roles
    }
    this.cdr.detectChanges(); // Ensure change detection runs to update the template
  }

  loadDropdownData(): Subject<void> {
    const dataLoaded = new Subject<void>();
    let regionsLoaded = false;
    let countriesLoaded = false;
    const checkAllLoaded = () => {
      if (regionsLoaded && countriesLoaded) {
        dataLoaded.next();
        dataLoaded.complete();
      }
    };
    this.apiService
      .getRegions()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: { regions: Region[] }) => {
          this.regions = response.regions;
          regionsLoaded = true;
          checkAllLoaded();
        },
        error: (err: HttpErrorResponse) => {
          console.error("Failed to load regions", err);
          dataLoaded.error(err);
        },
      });

    this.apiService
      .getCountries()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: { countries: Country[] }) => {
          this.allCountries = response.countries;
          this.countries = [...this.allCountries];
          countriesLoaded = true;
          checkAllLoaded();
        },
        error: (err: HttpErrorResponse) => {
          console.error("Failed to load countries", err);
          dataLoaded.error(err);
        },
      });
    return dataLoaded;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  goToAssessmentListing() {
    this.router.navigate(["/manage-assessment"]);
  }

  loadAssessmentDetails(id: number): void {
    this.loadingDetails = true;
    
    // Determine which API call to make based on whether historyId exists
    const apiCall = this.historyId 
      ? this.manageAssessmentApiService.getManageAssessmentByRcaHistory(id, this.historyId)
      : this.manageAssessmentApiService.getManageAssessmentByRca(id);
    
    apiCall
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.loadingDetails = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (data: any) => {
          this.assessmentDetails = { ...data };
          if (this.assessmentDetails) {
            this.assessmentDetails.change_types_count = this.assessmentDetails
              .change_types
              ? this.assessmentDetails.change_types.length
              : 0;
            this.assessmentDetails.product_families_count = this
              .assessmentDetails.product_families
              ? this.assessmentDetails.product_families.length
              : 0;
            this.totalItemsCount = this.assessmentDetails.progress?.total || 0;
            this.respondedItemsCount =
              this.assessmentDetails.progress?.completed || 0;
            this.pendingItemsCount =
              this.totalItemsCount - this.respondedItemsCount;
          }
        },
        error: (error) => {
          console.error("Error loading assessment details:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to load assessment details. Please try again.",
            life: 5000,
          });
          this.router.navigate(["/manage-assessment"]);
        },
      });
  }

  /**
   * Check if the issue date is in the future
   */
  private isIssueDateInFuture(): boolean {
    if (!this.assessmentDetails?.issue_date) {
      return false;
    }
    const issueDate = new Date(this.assessmentDetails.issue_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    return issueDate > today;
  }

  /**
   * Public method to check if response forms should be shown
   */
  shouldShowResponseForms(): boolean {
    return !this.isIssueDateInFuture();
  }

  initializeItemTableConfig(): void {
    this.itemColumns = [
      { field: "s_no", header: "S.No", sortable: false },
      {
        field: "version",
        header: "Version",
        sortable: false,
        hasVersionIcon: true,
      },
      { field: "region_name", header: "Region Name", sortable: false },
      { field: "country_name", header: "Country Name", sortable: false },
      {
        field: "local_ra",
        header: "Local RA",
        sortable: false,
        truncateAt: 20,
      },
      { field: "impact_status", header: "Impact Status", sortable: false },
      { field: "timestamp", header: "Timestamp", sortable: false },
      {
        field: "response_status",
        header: "Response Status",
        sortable: false,
        type: "status",
        isStatus: true,
      },
      {
        field: "actions",
        header: "Actions",
        sortable: false,
        isCustomButton: true,
      },
    ];
    this.itemGlobalFilterFields = [
      "region_name",
      "country_name",
      "local_ra",
      "impact_status",
      "response_status",
    ];
    this.itemFiltersConfig = [
      {
        key: "response_status",
        label: "Response Status",
        type: "multiselect",
        options: [
          { label: "Received", value: "Received" },
          { label: "Pending", value: "Pending" },
        ],
      },
      { key: "timestamp", label: "Timestamp Range", type: "daterange" },
    ];
    this.itemActions = [
      { label: "View", action: "ViewItem", icon: "pi pi-eye" },
      { label: "Email", action: "EmailItem", icon: "pi pi-envelope" },
    ];
  }

  onLazyLoadEventFromItemTable(event: LazyLoadEvent): void {
    // Don't load items if issue date is in the future
    if (this.isIssueDateInFuture()) {
      this.assessmentItems = [];
      this.itemTotalRecords = 0;
      this.loadingItems = false;
      this.cdr.detectChanges();
      return;
    }
    this.itemLastLazyLoadEvent = event;
    this.loadAssessmentItems(event);
  }

  loadAssessmentItems(event?: LazyLoadEvent, retainPageNumber = false): void {
    if (!this.assessmentId) return;
    
    this.loadingItems = true;
    this.cdr.detectChanges();
    const currentEvent = event ||
      this.itemLastLazyLoadEvent || { first: 0, rows: this.itemPageSize };
    this.itemPage = retainPageNumber ? this.itemPage :
      (currentEvent.first ?? 0) / (currentEvent.rows ?? this.itemPageSize) + 1;
    this.itemPageSize = currentEvent.rows ?? this.itemPageSize;
    const filtersToSend: any = {};

    // Add the global search term to filtersToSend if it exists
    if (this.itemGlobalSearchTerm) {
      filtersToSend["search"] = this.itemGlobalSearchTerm;
    }

    if (currentEvent.globalFilter && !filtersToSend["search"]) {
      filtersToSend.globalFilter = currentEvent.globalFilter;
    }
    if (currentEvent.filters) {
      for (const key in currentEvent.filters) {
        if (currentEvent.filters.hasOwnProperty(key) && key !== "global") {
          const filter = currentEvent.filters[key];
          if (
            filter.value !== null &&
            filter.value !== undefined &&
            filter.value !== ""
          ) {
            filtersToSend[key] = filter.value;
          }
        }
      }
    }
    for (const key in this.itemFiltersValue) {
      if (this.itemFiltersValue.hasOwnProperty(key)) {
        const value = this.itemFiltersValue[key];
        const filterConfig = this.itemFiltersConfig.find((f) => f.key === key);
        if (
          value !== null &&
          value !== undefined &&
          value !== "" &&
          (!Array.isArray(value) || value.length > 0)
        ) {
          if (
            (key === "response_status" ||
              key === "region_id" ||
              key === "country_id") &&
            filterConfig?.type === "multiselect" &&
            Array.isArray(value)
          ) {
            filtersToSend[`${key}_filter`] = value
              .map((item) => item.value)
              .join(",");
          } else if (key === "timestamp" && Array.isArray(value)) {
            filtersToSend["timestamp_from"] = value[0]
              ? this.datePipe.transform(value[0], "yyyy-MM-dd") || null
              : null;
            filtersToSend["timestamp_to"] = value[1]
              ? this.datePipe.transform(value[1], "yyyy-MM-dd") || null
              : null;
          } else {
            filtersToSend[key] = value;
          }
        }
      }
    }
    if (this.activeTab === "Responded") {
      filtersToSend.response_status = "Received";
    } else if (this.activeTab === "Pending") {
      filtersToSend.response_status = "Pending";
    } else {
      delete filtersToSend.response_status;
    }
    this.manageAssessmentApiService
      .getRcaDetailsResponse(
        this.assessmentId,
        this.itemPage,
        this.itemPageSize,
        filtersToSend,
        this.historyId || undefined
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: {
          response_forms: RawResponseForm[];
          meta: { total: number };
          metrics: {
            total_countries: number;
            responded_countries: number;
            pending_countries: number;
          };
        }) => {
          this.totalItemsCount = response?.metrics?.total_countries || 0;
          this.respondedItemsCount =
            response?.metrics?.responded_countries || 0;
          this.pendingItemsCount = response?.metrics?.pending_countries || 0;
          this.assessmentItems = response.response_forms.map(
            (item: RawResponseForm, index: number) => {
              const country = this.allCountries.find(c => c.id === item.country_id);
              // Prefer explicit item.region_id; if absent attempt to derive from country (assuming country.region_id exists)
              const regionId = item.region_id ?? (country as any)?.region_id;
              const region = this.regions.find(r => r.id === regionId);
              return {
                s_no: (this.itemPage - 1) * this.itemPageSize + index + 1,
                response_mapping_id: item.response_mapping_id,
                country_id: item.country_id,
                region_id: item.region_id,
                region_name: region ? region.name : "N/A",
                country_name: country ? country.name : "N/A",
                rca_id: this.assessmentId,
                local_ra:
                  item.ra_colleague_names
                    .filter((name) => name.trim() !== "")
                    .join(", ") || "N/A",
                impact_status: item.impact_status || "No",
                response_status: item.response_status,
                timestamp: this.formatDateTime(item.received_at_timestamp),
                version: `v${item.response_version_number}`,
                assigned_to_user_id: item.assigned_to_user_id,
              } as AssessmentItem;
            }
          );
          this.itemTotalRecords = response.meta.total;
          this.loadingItems = false;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.error("Error loading assessment items:", error);
          this.assessmentItems = [];
          this.itemTotalRecords = 0;
          this.loadingItems = false;
          this.cdr.detectChanges();
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to load assessment items. Please try again.",
            life: 5000,
          });
        },
        complete: () => {
          if (this.loadingItems) {
            this.loadingItems = false;
            this.cdr.detectChanges();
          }
        },
      });
  }

  onTabChange(tab: "Total" | "Responded" | "Pending"): void {
    if (this.activeTab === tab) {
      return;
    }
    // Don't allow tab change if issue date is in the future
    if (this.isIssueDateInFuture()) {
      return;
    }
    this.activeTab = tab;
    // Reset global search term when changing tabs
    this.itemGlobalSearchTerm = "";
    this.itemLastLazyLoadEvent = { first: 0, rows: this.itemPageSize };
    this.onItemsSearchClicked({});
    // this.loadAssessmentItems(this.itemLastLazyLoadEvent);
  }

  onItemsSearchClicked(filters: { [key: string]: any }): void {
    // Don't search if issue date is in the future
    if (this.isIssueDateInFuture()) {
      return;
    }
    const newFilters = { ...(this.activeTab === "Total" ? {} : this.itemFiltersValue), ...filters };
    if (this.activeTab === "Responded") {
      newFilters["response_status"] = [
        { label: "Received", value: "Received" },
      ];
    } else if (this.activeTab === "Pending") {
      newFilters["response_status"] = [{ label: "Pending", value: "Pending" }];
    } else {
      if (
        newFilters["response_status"] &&
        Array.isArray(newFilters["response_status"])
      ) {
        const config = this.itemFiltersConfig.find(
          (f) => f.key === "response_status"
        );
        if (config && config.type === "multiselect") {
          newFilters["response_status"] = newFilters["response_status"].filter(
            (item: any) =>
              config.options?.some((opt) => opt.value === item.value)
          );
          if (newFilters["response_status"].length === 0) {
            delete newFilters["response_status"];
          }
        }
      }
    }
    this.itemFiltersValue = newFilters;
    this.itemLastLazyLoadEvent = { first: 0, rows: this.itemPageSize };
    this.loadAssessmentItems(this.itemLastLazyLoadEvent);
  }

  // New method to handle global search input from the table
  onItemGlobalSearch(searchTerm: string): void {
    // Don't search if issue date is in the future
    if (this.isIssueDateInFuture()) {
      return;
    }
    this.itemGlobalSearchTerm = searchTerm;
    // Reset pagination to the first page when a global search is performed
    this.itemLastLazyLoadEvent = { first: 0, rows: this.itemPageSize };
    this.loadAssessmentItems(this.itemLastLazyLoadEvent);
  }

  onToggleItemAction(event: any): void {
    switch (event.action) {
      case "Mail":
        // Open the mail popup
        this.mailPopupData = event?.row || null;
        this.showMailPopupDialog = true;
        break;
      case "View":
      case "ViewReceived":
        // View Action item
        this.displayRcaDetailsPopup = true;
        this.mappingIdValue = event?.row || null;
        break;
      case "Start":
        this.displayRcaDetailsFormPopup = true;
        this.isReviseResponse = false;
        this, (this.mappingIdValue = event?.row || null);
        break;
      case "Assign":
      case "ChangeAssignee":
        this.displayRcaAssignPopup = true;
        this, (this.mappingIdValue = event?.row || null);
        break;
      case "RemoveAssignee":
        this.handleRemoveAssignee(event.row);
        break;
      case "Revise":
        this.showConfirmation("revise", event.row);
        break;
      case "Resume":
        this.displayRcaDetailsFormPopup = true;
        this.isReviseResponse = false;
        this.isReviseCheck = true
        this, (this.mappingIdValue = event?.row || null);
        break;
      default:
    }
  }

  formatDate(dateString: string | Date | undefined): string {
    if (!dateString) return "N/A";
    return this.datePipe.transform(dateString, "dd/MM/yyyy") || "N/A";
  }

  formatDateTime(dateTimeString: string | Date | undefined): string {
    if (!dateTimeString) return "N/A";
    return (
      this.datePipe.transform(dateTimeString, "dd/MM/yyyy, HH:mm:ss") || "N/A"
    );
  }

  getStatusClass(status: string): string {
    switch (status) {
      case "Received":
        return "status-received";
      case "Pending":
        return "status-pending";
      default:
        return "";
    }
  }

  getStatusStyles(status: string): StatusStyle {
    return this.statusStyleMap.get(status) || this.defaultStatusStyle;
  }

  handleExport(): void {
    if (!this.assessmentId) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Assessment ID is missing. Cannot export.",
        life: 3000,
      });
      return;
    }
    // Don't export if issue date is in the future
    if (this.isIssueDateInFuture()) {
      this.messageService.add({
        severity: "info",
        summary: "Not Available",
        detail: "Response forms are not yet issued for export.",
        life: 3000,
      });
      return;
    }
    const assessmentId = this.assessmentId;
    if (this.shouldWarnBeforeExport()) {
      this.showConfirmation("export_without_attachment", null);
      return;
    }
    this.executeExport(assessmentId);
  }

  private shouldWarnBeforeExport(): boolean {
    let isLocalRA = this.currentUserRole === this.UserRole.LocalRA || 
      this.currentUserRole === this.UserRole.LocalRA1;
      
    return !this.assessmentItems.some(
      (item) => item.response_status ===  (isLocalRA ?  "Submitted" : "Received")
    );
  }

  private executeExport(assessmentId: number): void {
    this.isExporting = true; // Disable export button
    const exportFilters: any = {};
    // Apply current filters from itemFiltersValue
    for (const key in this.itemFiltersValue) {
      if (this.itemFiltersValue.hasOwnProperty(key)) {
        const value = this.itemFiltersValue[key];
        if (value !== null && value !== undefined && value !== "") {
          if (key === "response_status" && Array.isArray(value)) {
            exportFilters.response_status_filter = value.map((item: any) => item.value).join(',');
          } else if (key === "timestamp" && Array.isArray(value) && value.length === 2) {
            // Format date range for API
            exportFilters.date_from = value[0] ? this.datePipe.transform(value[0], "yyyy-MM-dd") : null;
            exportFilters.date_to = value[1] ? this.datePipe.transform(value[1], "yyyy-MM-dd") : null;
          }
        }
      }
    }

    // Apply active tab filter
    if (this.activeTab === "Responded") {
      exportFilters.response_status_filter = 'Received';
    } else if (this.activeTab === "Pending") {
      exportFilters.response_status_filter = 'Pending';
    }
    // Add global search term if it exists
    if (this.itemGlobalSearchTerm) {
      exportFilters.search = this.itemGlobalSearchTerm;
    }
    this.manageAssessmentApiService.exportRcaResponses(assessmentId, exportFilters, false, this.historyId)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isExporting = false; // Re-enable export button
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: (response: HttpResponse<Blob>) => {
          const blob = response.body;
          if (!blob) {
            this.messageService.add({
              severity: "error",
              summary: "Export Failed",
              detail: "No file data received from the server.",
              life: 5000,
            });
            return;
          }
          
          // Determine file type from Content-Type header
          const contentType = response.headers.get('Content-Type');
          const isZipped = contentType?.includes('application/zip') || contentType?.includes('application/x-zip-compressed');
          
          // Set default filename based on content type (fallback if Content-Disposition is unavailable)
          const versionSuffix = this.assessmentDetails?.version_number ? `_v${this.assessmentDetails.version_number}` : '';
          let filename = `RCA_Responses_${this.assessmentDetails?.change_assess_no || 'Export'}${versionSuffix}${isZipped ? '.zip' : '.xlsx'}`;
          
          // Attempt to extract filename from Content-Disposition header
          const contentDisposition = response.headers.get('Content-Disposition');
          if (contentDisposition) {
            // Try different patterns to extract filename
            let filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i);
            if (!filenameMatch) {
              filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/i);
            }
            if (!filenameMatch) {
              filenameMatch = contentDisposition.match(/filename=([^;\n]+)/i);
            }
            
            if (filenameMatch && filenameMatch[1]) {
              try {
                // Decode URI component to handle potential URL-encoded characters
                filename = decodeURIComponent(filenameMatch[1].trim().replace(/['"]/g, ''));
              } catch (e) {
                console.warn('Could not decode filename from Content-Disposition, using raw:', filenameMatch[1]);
                filename = filenameMatch[1].trim().replace(/['"]/g, '');
              }
            }
          }
          
          saveAs(blob, filename); // Use file-saver to download the blob
          this.messageService.add({
            severity: "success",
            summary: "Export Successful",
            detail: "Data exported successfully.",
            life: 3000,
          });
        },
        error: (error: HttpErrorResponse) => {
          console.error("Error during export:", error);
          this.messageService.add({
            severity: "error",
            summary: "Export Failed",
            detail: error.error?.message || "An error occurred during export. Please try again.",
            life: 5000,
          });
        },
      });
  }

  onRcaDetailsDialogClose(): void {
    this.displayRcaDetailsPopup = false;
    this.mappingIdValue = null;
  }

  onAssessmentDetailsDialogClose(): void {
    this.displayAssessmentDetailsPopup = false;
    this.assessmentDetailsPopupData = null;
  }

  /**
   * Handle view button click for assessment details
   * Opens the assessment details popup with all RCA information
   */
  onViewAssessmentDetails(): void {
    if (this.assessmentDetails && this.assessmentId) {
      // Prepare the data structure expected by the popup
      this.assessmentDetailsPopupData = {
        rca_id: this.assessmentId,
        response_mapping_id: null, // No response mapping for the main assessment view
      };
      
      this.displayAssessmentDetailsPopup = true;
      this.cdr.detectChanges();
    } else {
      console.warn('Cannot open assessment details - missing data:', {
        assessmentDetails: this.assessmentDetails,
        assessmentId: this.assessmentId
      });
    }
  }

  // New methods for Overlay Panel
  toggleOverlay(event: Event, data: string[], title: string): void {
    this.overlayPanelData = data;
    this.overlayPanelTitle = title;
    this.overlayPanel.toggle(event);
    this.cdr.detectChanges();
  }

  onRcaDetailsFormDialogClose(event: any): void {
    this.displayRcaDetailsFormPopup = false;
    this.isReviseResponse = false;
    this.isReviseCheck = false;
    this.mappingIdValue = null;
    if (event && Object.keys(event).length > 0) {
      this.showMailPopupDialog = false;
      this.showCustomerDetailsSuccessDialog = true;
      this.loadAssessmentItems({ first: 0, rows: this.itemPageSize });
      this.customerDetailsSuccessMessage = event?.action || "Response form submitted successfully!";
    }
  }

  onRcaAssignDialogClose(): void {
    this.displayRcaAssignPopup = false;
  }
  
  assignSuccess(event:any): void {
    this.customerDetailsSuccessDialogTitle = 'Response Assign'
    this.showCustomerDetailsSuccessDialog = true;
    this.customerDetailsSuccessMessage = `${event.response.message}!`;
    this.loadAssessmentItems({ first: 0, rows: this.itemPageSize }, true);
    this.mappingIdValue = null;
  }

  handleRemoveAssignee(rowData: any): void {
    if (!rowData?.response_mapping_id) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Invalid response mapping ID",
        life: 5000,
      });
      return;
    }

    const apiPayload = {
      user_id: null,
      allow_reassignment: true,
      unassign: true
    };

    this.manageAssessmentApiService
      .submitRcaResponseToAssignee(apiPayload, rowData.response_mapping_id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.customerDetailsSuccessDialogTitle = 'Success';
          this.showCustomerDetailsSuccessDialog = true;
          this.customerDetailsSuccessMessage = `${response.message}!`;
          this.loadAssessmentItems({ first: 0, rows: this.itemPageSize }, true);
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to remove assignee:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to remove assignee. Please try again.",
            life: 5000,
          });
        },
      });
  }

  onMailPopupClose() {
    this.showMailPopupDialog = false;
  }
  onEmailSent(event: any) {
    this.showMailPopupDialog = false;
    this.showCustomerDetailsSuccessDialog = true;
    this.customerDetailsSuccessMessage = "Email sent successfully!";
  }
  onCustomerDetailsSuccessDialogClose() {
    this.showCustomerDetailsSuccessDialog = false;
  }
  // This method will be called by the app-table whenever the selection changes
  onSelectedItemsChange(selectedItems: any[]): void {
    this.selectedAssessmentItems = selectedItems;
    // Update the visibility of the action button based on selection count
    this.showBulkActionButton = this.selectedAssessmentItems.length > 0;
    this.cdr.detectChanges();
  }

  handleBulkAction(): void {
    if (this.selectedAssessmentItems.length > 0) {
      this.showConfirmation("no_impact", this.selectedAssessmentItems);
    } else {
      this.messageService.add({
        severity: "warn",
        summary: "No Selection",
        detail: "Please select at least one item to perform a bulk action.",
        life: 3000,
      });
    }
  }

  showConfirmation(actionType: ConfirmationActionType, row: any): void {
    this.pendingAction = { type: actionType, row: row };

    switch (actionType) {
      case "no_impact":
        this.confirmationConfig = {
          header: "No Impact Submission",
          message: `You are about to submit multiple assessments marking it as No Impact?.`,
          acceptButtonLabel: "Submit",
          rejectButtonLabel: "Cancel",
          icon: "pi pi-exclamation-triangle",
          acceptButtonStyleClass: "p-button-warning",
        };
        break;
      case "export_without_attachment":
        this.confirmationConfig = {
          header: "No Attachment",
          message: "There are no responses available to export. Are you sure you wish to continue?.",
          acceptButtonLabel: "Export",
          rejectButtonLabel: "Cancel",
          icon: "pi pi-exclamation-triangle",
          acceptButtonStyleClass: "p-button-warning",
        };
        break;
      case "revise":
        this.confirmationConfig = {
          header: "Revise Assessment",
          message: `Are you sure you want to revise this response, that you have already created?.`,
          acceptButtonLabel: "Yes",
          rejectButtonLabel: "Cancel",
          icon: "pi pi-exclamation-triangle",
          acceptButtonStyleClass: "p-button-warning",
        };
        break;
      default:
        this.confirmationConfig = null;
        this.pendingAction = null;
        return;
    }
    this.showConfirmationPopup = true;
    this.cdr.detectChanges();
  }

  onConfirmation(confirmed: any): void {
    if (confirmed && this.pendingAction) {
      const { type, row } = this.pendingAction;
      switch (type) {
        case "no_impact":
          this.markAsNoImpact(row);
          break;
        case "export_without_attachment":
          if (this.assessmentId) {
            this.executeExport(this.assessmentId);
          }
          break;
        case "revise":
          this.executeRevise(row);
          break;
      }
    }
    // Reset pending action and hide popup regardless of confirmation
    this.pendingAction = null;
    this.showConfirmationPopup = false;
  }

  private executeRevise(row: any): void {
    const event = {
      row,
      action: "Revise",
    };
    this.displayRcaDetailsFormPopup = true;
    this.isReviseResponse = true;
    this, (this.mappingIdValue = event?.row || null);
  }

  private markAsNoImpact(row: any): void {
    const mappingId: number[] =
      row?.map((item: any) => item?.response_mapping_id) || [];
    const payloadObj = {
      response_mapping_ids: mappingId,
    };
    this.manageAssessmentApiService
      .updateRcaAsNoImpact(payloadObj)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          this.customerDetailsSuccessDialogTitle = 'No Impact';
          this.showCustomerDetailsSuccessDialog = true;
          this.customerDetailsSuccessMessage =
            "Response form successfully marked as no impact!";
          this.loadAssessmentItems({ first: 0, rows: this.itemPageSize });
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to mark as No Impact:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to mark as No Impact. Please try again.",
            life: 5000,
          });
        },
      });
  }
  
  handleResponseDraft(event: any) {
    this.customerDetailsSuccessDialogTitle = 'Response Draft'
    this.showCustomerDetailsSuccessDialog = true;
    this.displayRcaDetailsFormPopup=false
    this.customerDetailsSuccessMessage = `Response progress saved successfully!`;
    this.loadAssessmentItems({ first: 0, rows: this.itemPageSize });
    this.mappingIdValue = null;
  }

  /**
   * Handle view button click for version history
   * Opens the RCA details for the specific version
   */
  onViewVersionHistory(versionItem: any): void {
    if (!versionItem || !versionItem.id) {
      this.messageService.add({
        severity: "error",
        summary: "Error",
        detail: "Invalid version data.",
        life: 3000,
      });
      return;
    }

    // Navigate to the same route with history_id query parameter
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { 
        fromVersion: true,
        fromAction: true,
        history_id: versionItem.id
      },
      queryParamsHandling: 'merge'
    });

    // Reload the assessment details with the history_id
    this.historyId = versionItem.id;
    this.loadAssessmentDetails(this.assessmentId!);
  }

  enableGlobalSearch(): boolean {
    return true;
  }
}
